import React from 'react';
import { Box, Form, Heading, SubmitButton, Text, useToast } from '@medely/ui-kit';
import { ReferenceForm } from './components/ReferenceForm';
import { referenceValidationSchema } from './schema';
import { FixedFooterLayout } from '../FixedFooterLayout';
import { ReferenceFormValues, ReferenceFormContainerProps } from './components/interface';
import { useReferenceForViewPage, useWorkHistoriesForReferenceForm } from './graphql';
import { useMutationsReferences } from './mutations';
import { ContentLoader } from '../ContentLoader';
import { useProNavigate } from '@mp/contexts';
import { formatReferencePayload, formatWorkHistoryPayload, getDefaultValues } from './utils';

export const ReferenceFormContainer = ({ id }: ReferenceFormContainerProps) => {
  const { reference, isLoading: isLoadingReference } = useReferenceForViewPage(id);
  const toast = useToast();

  const [isSavingNewReferenceWithWorkHistory, setIsSavingNewReferenceWithWorkHistory] =
    React.useState(false);

  const { navigate } = useProNavigate();
  const { addReference, editReference, addWorkHistories, isLoading } = useMutationsReferences();
  const {
    workHistories: allWorkHistories,
    isLoading: isLoadingWorkHistories,
    refetch,
  } = useWorkHistoriesForReferenceForm();
  const workHistories = allWorkHistories.filter(({ clinical }) => !!clinical);

  const isEditing = Boolean(reference?.id);

  const handleSubmit = async (values: ReferenceFormValues) => {
    const needsToCreateNewWorkHistory =
      values.facility_name === 'new_facility' || values.department === 'add_new_unit';

    const referenceValues = formatReferencePayload({ values, workHistories });
    const workHistoryValues = formatWorkHistoryPayload(values);

    if (needsToCreateNewWorkHistory) {
      setIsSavingNewReferenceWithWorkHistory(true);
      await addWorkHistories(
        { values: workHistoryValues },
        {
          onSuccess: async () => {
            const refreshed = await refetch();
            const refreshedWorkHistories = refreshed?.data?.workHistories ?? [];
            const organizationName = values.facility_name_new ?? values.facility_name;
            const unitName = values.department_new ?? values.department;
            const match = refreshedWorkHistories.find(
              (wh) => wh.organization_name === organizationName && wh.unit === unitName,
            );

            if (!match?.id) {
              toast.error('We had an issue creating the work history. Please try again.');
              setIsSavingNewReferenceWithWorkHistory(false);
              return;
            }

            const updatedReferenceValues = {
              ...referenceValues,
              work_history_id: match.id,
            };

            (isEditing ? editReference : addReference)(
              { values: { ...updatedReferenceValues, id: reference?.id } },
              {
                onSuccess: () => {
                  navigate('ResumeBuilder');
                  setIsSavingNewReferenceWithWorkHistory(false);
                },
                onError: () => {
                  setIsSavingNewReferenceWithWorkHistory(false);
                },
              },
            );
          },
          onError: () => {
            toast.error('We had an issue creating the work history. Please try again.');
            setIsSavingNewReferenceWithWorkHistory(false);
          },
        },
      );
      return;
    }

    (isEditing ? editReference : addReference)(
      { values: { ...referenceValues, id: reference?.id } },
      {
        onSuccess: () => {
          navigate('References');
        },
      },
    );
  };

  const title = isEditing ? 'Edit reference' : 'Add reference';
  const loadingInitialData = isEditing
    ? isLoadingWorkHistories || isLoadingReference
    : isLoadingWorkHistories;

  return (
    <ContentLoader loading={loadingInitialData}>
      <Form
        key={`reference-form-${reference?.id ?? 'new'}`}
        handleSubmit={handleSubmit}
        schema={referenceValidationSchema}
        defaultValues={getDefaultValues({ reference: reference || undefined })}
      >
        <FixedFooterLayout
          footerContent={
            <Box py={1}>
              <SubmitButton
                fullWidth
                testId="work-gap-modal-save-button"
                disabled={isLoading || isSavingNewReferenceWithWorkHistory}
                loading={isLoading || isSavingNewReferenceWithWorkHistory}
              >
                Save
              </SubmitButton>
            </Box>
          }
          height="97%"
        >
          <Box pt={2}>
            <Heading size="xs">{title}</Heading>
          </Box>
          <Text size="m">
            References should be direct supervisors in a clinical setting within the last 3 years.
          </Text>
          <ReferenceForm workHistories={workHistories} />
        </FixedFooterLayout>
      </Form>
    </ContentLoader>
  );
};
